import {
  Engagement,
  EngagementPrepaymentResponse,
  EngagementStepStatus,
  EngagementStepType,
  EngagementTaskType,
  isTaskCompleted,
} from '@aiwyn/api';

/**
 * The five UX states the Payment Information step models for a prepayment (PP-3635):
 *  - `default`   — nothing charged yet (collect a method + Authorize), or a non-failure summary.
 *  - `processing` — a bank-debit (ACH/ACSS/BACS) prepayment is pending; read-only, Authorize disabled.
 *  - `completed`  — the prepayment cleared (paid).
 *  - `failed`     — the prepayment hard-failed (card decline); the method picker re-opens for retry.
 *  - `waiting`    — payment info confirmed but the charge is deferred until all signatures are
 *                   collected; no charge attempted yet. Derived (not persisted) via {@link isAwaitingSignatures}.
 */
export type PrepaymentStepState = 'default' | 'processing' | 'completed' | 'failed' | 'waiting';

/**
 * Count outstanding signature requirements on an engagement — SIGN_ENGAGEMENT_DOCUMENT tasks that are
 * not yet completed (COMPLETED / COMPLETED_OPTIONAL both count as done). Derived in-memory; there is no
 * backend "outstanding count" field (ART-8130). Multi-identity dedup in mapHubToEngagement ensures each
 * signatory's sign-task is represented once.
 */
export function outstandingSignatureCount(engagement: Engagement | undefined): number {
  const steps = engagement?.report?.steps ?? [];
  return steps.reduce(
    (count, step) =>
      count +
      step.tasks.filter(
        task => task.type === EngagementTaskType.SIGN_ENGAGEMENT_DOCUMENT && !isTaskCompleted(task.status),
      ).length,
    0,
  );
}

/**
 * Single source of truth for the Waiting state (PP-3635): the client has confirmed the payment info step
 * but one or more signatures are still outstanding, so the prepayment charge is deferred.
 *
 * `paymentInfoConfirmed` is derived from the PAYMENT step's own COMPLETE status — NOT
 * `report.paymentInfoRequirementMet`, which `mapHubToEngagement` synthesizes from `allStepsComplete` and
 * so is `false` while signatures are still outstanding. Single-signer / already-complete engagements have
 * zero outstanding signatures and therefore never enter Waiting (the charge fires immediately).
 */
export function isAwaitingSignatures(engagement: Engagement | undefined): boolean {
  const paymentStep = engagement?.report?.steps?.find(step => step.type === EngagementStepType.PAYMENT);
  const paymentInfoConfirmed = paymentStep?.status === EngagementStepStatus.COMPLETE;
  return paymentInfoConfirmed && outstandingSignatureCount(engagement) > 0;
}

/**
 * Waiting banner copy, pluralized by the outstanding-signature count (singular when one remains).
 * Exact strings pending final design sign-off (FIRM-10915).
 */
export function awaitingSignaturesMessage(outstandingCount: number): string {
  return outstandingCount === 1
    ? 'Everything is set and payment will be attempted once the remaining signature has been collected.'
    : 'Everything is set and payment will be attempted once all signatures have been collected.';
}

/**
 * Derive the {@link PrepaymentStepState} from the loaded prepayment and the engagement.
 *
 * `prepaymentStatus` (ART-8130 AC3) is authoritative and is read off the engagement (hub-sourced via
 * mapHubToEngagement); the legacy `active === false` signal is only consulted as a migration fallback when
 * status is absent. When the sync-prepayment LD flag is off we always return `default` so the legacy
 * "Pay Now → route out" behavior is preserved untouched.
 */
export function derivePrepaymentStepState(
  prepayment: EngagementPrepaymentResponse | undefined,
  syncPrepaymentEnabled: boolean,
  engagement?: Engagement,
): PrepaymentStepState {
  if (!syncPrepaymentEnabled) {
    return 'default';
  }

  // The engagement's hub-sourced status is the authority. The prepayment read model carries only a copy of
  // it — mergeHubPrepaymentStatus stamps the copy from whatever the engagement held when that response
  // landed — so the copy can only ever agree with the hub or lag behind it, never know better. It lagged
  // on an in-place retry: the copy is stamped while the engagement still reads FAILED, so once the hub
  // cleared the status the copy dragged the step back into the failure UX (PP-3794). Given an engagement
  // to read, the copy therefore gets no say; it is consulted only when there is no engagement at all.
  // Note a cleared status arrives as `undefined`, not `null` — the hub response serializes NON_NULL, so it
  // omits the field outright.
  const prepaymentStatus = engagement != null ? engagement.prepaymentStatus : prepayment?.prepaymentStatus;

  switch (prepaymentStatus) {
    case 'FAILED':
      return 'failed';
    case 'PROCESSING':
      return 'processing';
  }

  // status is authoritative; only fall back to the legacy `active` flag when it is absent.
  if (prepayment?.prepaymentStatus == null && prepayment?.active === false) {
    return 'failed';
  }

  if (prepayment?.active && prepayment?.allocationDate != null) {
    return 'completed';
  }

  return 'default';
}
